import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { Page as PDFPage } from 'react-pdf';
import { throttle } from 'throttle-debounce';
import { listLoop } from '@/lib/helpers';
import { updateStyle } from '@/lib/domHelpers';
import { getTransform, angles } from '@/pages/document/DocumentViewer/helpers';

import Box from '@mui/material/Box';
import DocumentPageControls from '@/pages/document/components/DocumentPageControls';
import {
  PageArea,
  PageWrapper,
} from '@/pages/dokkaPay/pages/payment/components/PaymentComponent/components/Page/StyledComponents';

const BORDER_WIDTH = 30;
const MAX_DOC_WIDTH = 870;

const stopPropagationCallback = (cb: () => void) => (e: React.SyntheticEvent) => {
  e.stopPropagation();
  cb();
};

interface PageParams {
  rotate: number;
  embdRotate: number;
  height: number;
  idx: number;
  status: string | null;
}

interface PageProps {
  params: PageParams;
  idx: number;
  onRenderSuccess: (paymentId: string, data: unknown) => void;
  scale: number;
  width: number;
  total: number;
  onRotate: (idx: number, angle: number) => void;
  onZoom: (type: string) => void;
  onReset: () => void;
  getIsDisabledZoomButton: (type: string) => boolean;
  container: HTMLElement | null | undefined;
  paymentId: string;
  onScale: (scale: number) => void;
}

const Page: React.FC<PageProps> = ({
  params,
  idx,
  onRenderSuccess,
  scale,
  width,
  total,
  onRotate,
  onZoom,
  onReset,
  getIsDisabledZoomButton,
  container,
  paymentId,
  onScale,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const startPosition = useRef({ x: 0, y: 0 });
  const position = useRef({ x: 0, y: 0 });
  const shiftPositionX = useRef(50);
  const isMoveRef = useRef(false);
  const wheelDeltaRef = useRef(0);

  const scrollTop = container ? container.scrollTop : 0;

  const rotateAngle = useMemo(() => {
    return (params.rotate + params.embdRotate) % 360;
  }, [params.rotate, params.embdRotate]);

  const pageWidth = useMemo(() => {
    return width > MAX_DOC_WIDTH + BORDER_WIDTH ? MAX_DOC_WIDTH : width - BORDER_WIDTH;
  }, [width]);

  const getShftSizeX = useCallback(() => {
    const viewPortSize = container ? container.offsetWidth : width;
    const currentPageWidth = pageWidth * scale;
    return Math.max(currentPageWidth - viewPortSize, 0);
  }, [pageWidth, container, scale, width]);

  const scalePosition = useCallback(() => {
    const shiftX = -Math.abs(Math.max(getShftSizeX(), 0)) * (shiftPositionX.current / 100);
    position.current = { ...position.current, x: shiftX };
  }, [getShftSizeX]);

  const updateCursor = (value: string | null) => {
    if (containerRef.current) {
      updateStyle(containerRef.current, [{ name: 'cursor', value }]);
    }
  };

  const updateShiftPositionX = (shiftPx: number) => {
    shiftPositionX.current = Math.max(Math.min(100, (100 / getShftSizeX()) * Math.abs(shiftPx)), 0);
  };

  const movePage = () => {
    if (containerRef.current && isMoveRef.current) {
      const { x, y } = position.current;
      updateStyle(containerRef.current, [{ name: 'transform', value: `translate3d(${x}px, 0, 0)` }]);
      if (container) container.scrollTo(0, y);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const scrollTopAtMouseDown = container ? container.scrollTop : 0;

    startPosition.current = {
      x: position.current.x ? e.clientX - position.current.x : e.clientX,
      y: scrollTopAtMouseDown + e.clientY,
    };
    isMoveRef.current = true;
    updateCursor('-webkit-grabbing');
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isMoveRef.current) {
      const { clientX, clientY } = event;
      const { x, y } = startPosition.current;
      const shiftSizeX = getShftSizeX();
      const moveX = clientX - x;
      const posX = shiftSizeX > 0 ? Math.max(-Math.abs(shiftSizeX), Math.min(moveX, 0)) : 0;
      updateShiftPositionX(posX);
      position.current = {
        x: posX,
        y: y - clientY,
      };
      window.requestAnimationFrame(movePage);
    }
  };

  const handleMouseUp = () => {
    isMoveRef.current = false;
    updateCursor(null);
  };

  const handleScale = useCallback(() => {
    onScale(scale + wheelDeltaRef.current);
  }, [onScale, scale]);

  const throttledScale = useMemo(() => throttle(200, false, handleScale), [handleScale]);

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      const { deltaY } = event;
      wheelDeltaRef.current = -(deltaY / Math.abs(deltaY)) * 0.05;
      throttledScale();
    }
  };

  useEffect(() => {
    if (scale === 1) {
      shiftPositionX.current = 50;
    }
    scalePosition();
  }, [scale, scalePosition]);

  const loop = useMemo(() => listLoop(params.rotate / 90, angles), [params.rotate]);
  const onRotateLeft = stopPropagationCallback(() => onRotate(idx, loop.prev));
  const onRotateRight = stopPropagationCallback(() => onRotate(idx, loop.next));

  const pageStyle = useMemo(
    () => ({
      width: Math.ceil(pageWidth * scale),
      height: Math.ceil(params.height),
      transform: getTransform(position.current.x),
    }),
    [params.height, scale, position.current.x, pageWidth],
  );

  const onRender = useCallback(
    (props: any) => {
      const { _pageInfo } = props;
      const { view } = _pageInfo;
      onRenderSuccess(paymentId, {
        ...props,
        pageInfo: { ..._pageInfo, view },
        pageIndex: idx,
      });
    },
    [onRenderSuccess, paymentId, idx],
  );

  const isSmallActionButton = width <= 450;
  const pageContainerId = `pdfPage-${idx + 1}`;

  return (
    <Box sx={{ position: 'relative', height: Math.ceil(params.height) }}>
      <PageWrapper ref={wrapperRef} sx={{ height: Math.ceil(params.height) }}>
        <PageArea
          id={pageContainerId}
          role="presentation"
          style={pageStyle}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseUp}
          onMouseUp={handleMouseUp}
          onWheel={handleWheel}
          ref={containerRef}
        >
          <PDFPage
            renderTextLayer={false}
            width={pageWidth}
            onRenderSuccess={onRender}
            rotate={rotateAngle}
            scale={scale}
            renderAnnotationLayer
            pageNumber={idx + 1}
          />
        </PageArea>
      </PageWrapper>
      {/* @ts-expect-error JS component lacks TypeScript types */}
      <DocumentPageControls
        total={total}
        currentNumber={idx + 1}
        isStatement={false}
        onRotateLeft={onRotateLeft}
        onRotateRight={onRotateRight}
        isSmallActionButton={isSmallActionButton}
        onZoom={onZoom}
        onReset={onReset}
        getIsDisabledZoomButton={getIsDisabledZoomButton}
        stopPropagationCallback={stopPropagationCallback}
        pageWidth={pageWidth}
      />
    </Box>
  );
};

export default Page;
